/**
 * `/tenants/:tenantId` — one workspace, and everything staff may do to it.
 *
 * Before this screen existed there was no way to correct a workspace at all:
 * `admin/tenants` had `POST`, `GET` and `GET /:tenantId`, and a misspelled name,
 * a wrong contact address or a subscription that needed extending could only be
 * fixed with an `UPDATE` typed into a database console — unattributed, with no
 * reason attached, by whoever happened to have the credentials. That is the gap
 * this screen closes, and it is why every mutation on it demands an explanation
 * before it will submit.
 *
 * FOUR PANELS, IN THIS ORDER, AND THE ORDER IS AN ARGUMENT:
 *
 *   1. **State** — can this workspace sign in, and until when. It is the first
 *      thing anyone opening a tenant is actually here to find out, and it is the
 *      thing two of the fields below silently control.
 *   2. **Details** — the correction form. The largest panel, because it is the
 *      one with the most ways to be wrong.
 *   3. **Credits** — balances and the top-up, together on one panel so a number
 *      is never typed without the current value in view.
 *   4. **What staff cannot change**, then the **detail-change history**. The
 *      boundary comes before the history because it answers the question the
 *      form provokes ("why can't I edit that?"), and the history closes the
 *      screen because it is what the Details form writes into.
 *
 *      It is **not** what the Credits panel writes into, and the panel is
 *      titled accordingly. A grant's audit row goes to `audit_log` rather than
 *      `admin_audit` — a privilege constraint argued in full in
 *      `admin-credits.service.ts` — and no screen reads `audit_log` yet. A list
 *      that closes the screen under a heading implying it covers everything
 *      above it would answer "why is this balance 50,000?" with "nobody granted
 *      anything", which is the one wrong answer this screen can give.
 *
 * THE SCREEN RE-READS THE TENANT AFTER A SAVE RATHER THAN TRUSTING ITS OWN COPY.
 * `updateTenantResponse` carries the corrected tenant and it would be cheaper to
 * render that, but the form is a diff measured against what is stored, and a
 * baseline held in this component's state is a baseline that can quietly go
 * stale — two staff members on the same workspace, or any write from anywhere
 * else. Re-reading costs one request and keeps exactly one answer on screen.
 *
 * THE OUTCOME OF A SAVE IS REPORTED IN WORDS, INCLUDING THE EMPTY ONE.
 * `changes` comes back computed against the stored row, so it can be empty even
 * for a request that named three fields. "No field differed" and "3 fields
 * updated" are different statements and this screen makes them look different —
 * rendering the empty case as a green "Saved" would teach staff that the button
 * works when it has done nothing, which is how the next real failure gets
 * missed.
 *
 * THE BLUEPRINT LOOK (slice 66) changes how the panels are drawn and nothing
 * about what they say or do. The state at the top is `@wend/ui`'s Badge (the
 * local `Pill` it replaces was the same classes under another name), the
 * features are `FeatureTags`, and the facts' labels are the product's kicker
 * type. The panels below say what they adopted in their own headers.
 */
import { ArrowLeft, RotateCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import type { Tenant, UpdateTenantResponse } from '@wend/shared';
import { Badge, Button, Callout, Card, CardContent, CardHeader, CardTitle, Kicker, Spinner } from '@wend/ui';

import { AdminShell } from '../../components/AdminShell';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { FeatureTags } from '../../components/FeatureTags';
import { describeApiError } from '../../lib/api-error';
import { formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { subscriptionState } from '../../lib/tenant-status';
import { TENANT_FIELD_LABEL, formatAuditValue } from '../../lib/tenant-fields';
import { getTenant } from '../../lib/tenants-api';
import { useAsyncData } from '../../lib/use-async-data';
import { TenantAuditPanel } from './TenantAuditPanel';
import { TenantBoundaryPanel } from './TenantBoundaryPanel';
import { TenantCreditsPanel } from './TenantCreditsPanel';
import { TenantEditForm } from './TenantEditForm';
import { TenantUsagePanel } from './TenantUsagePanel';

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();

  // A route that cannot name a tenant is not a tenant screen. This is only
  // reachable through a malformed link, so it goes back to the list rather than
  // rendering a shell around an error nobody caused.
  if (tenantId === undefined) return <Navigate to="/tenants" replace />;

  return <TenantDetail tenantId={tenantId} />;
}

function TenantDetail({ tenantId }: { tenantId: string }) {
  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) => getTenant(tenantId, signal),
    [tenantId],
  );

  /** The API's own account of the last save. Cleared when another one starts. */
  const [lastSave, setLastSave] = useState<UpdateTenantResponse | null>(null);
  /**
   * Bumped so the history re-reads after this screen writes something **into
   * `admin_audit`** — which today is a detail correction and nothing else.
   */
  const [historyToken, setHistoryToken] = useState(0);

  const tenant = data?.tenant ?? null;
  const isFirstLoad = status === 'loading' && tenant === null;

  const backLink = (
    <Link
      to="/tenants"
      className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted transition-colors hover:text-text"
    >
      <ArrowLeft aria-hidden="true" className="size-3.5" />
      Tenants
    </Link>
  );

  const onSaved = (result: UpdateTenantResponse): void => {
    setLastSave(result);
    // Only a save that moved something wrote an audit row; re-reading the
    // history after one that did not would present an unchanged list as though
    // it had just been refreshed for a reason.
    if (result.changes.length > 0) setHistoryToken((value) => value + 1);
    reload();
  };

  if (isFirstLoad) {
    return (
      <AdminShell title="Tenant">
        <div className="flex max-w-4xl flex-col gap-5">
          {backLink}
          <Card>
            <div className="flex items-center justify-center gap-3 px-6 py-16 text-muted">
              <Spinner size="lg" label="Loading this workspace" />
            </div>
          </Card>
        </div>
      </AdminShell>
    );
  }

  if (tenant === null) {
    return (
      <AdminShell title="Tenant">
        <div className="flex max-w-4xl flex-col gap-5">
          {backLink}
          <Card>
            {/*
              `error` is non-null here in every path that can actually happen —
              `getTenant` either resolves with a tenant or rejects — but the
              fallback is written out rather than asserted away with a `!`,
              because a screen that throws while reporting a failure reports
              nothing at all.
            */}
            {error !== null ? (
              <ErrorState error={error} title="Could not load this workspace" onRetry={reload} />
            ) : (
              <div role="alert" className="flex flex-col items-start gap-3 px-6 py-10">
                <p className="max-w-prose text-[0.95rem] leading-relaxed text-muted">
                  This workspace could not be loaded, and the API did not say why.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RotateCw aria-hidden="true" />}
                  onClick={reload}
                >
                  Try again
                </Button>
              </div>
            )}
          </Card>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title={tenant.name}>
      <div className="flex max-w-4xl flex-col gap-5">
        {backLink}

        {/*
          A failed reload over a workspace that is already on screen. Said out
          loud, because everything below is being edited against these values
          and a silent failure would let somebody save a diff measured from a
          stale baseline.
        */}
        {status === 'error' && error !== null && (
          <FormAlert requestId={error.requestId}>
            {`${describeApiError(error)} What is shown below is the last version that loaded, and the form below measures its changes against it — reload before saving.`}
          </FormAlert>
        )}

        <TenantHeader tenant={tenant} refreshing={refreshing} />

        {lastSave !== null && <SaveOutcome result={lastSave} />}

        {/*
          Keyed on `updatedAt`, so a save that actually changed something
          remounts the form against the freshly-read tenant — new baseline, empty
          reason. A save that changed nothing leaves `updatedAt` alone and the
          operator keeps what they typed, which is what they need in order to
          work out why it changed nothing.
        */}
        <TenantEditForm key={tenant.updatedAt} tenant={tenant} onSaved={onSaved} />

        {/*
          No `onGranted`. A top-up writes no `admin_audit` row — the movement
          runs on the RLS-enforced connection, which holds nothing on that table,
          so the grant is audited into `audit_log` instead — and the history
          panel below reads `admin_audit` only. Bumping `historyToken` here made
          a list that cannot have changed visibly re-read after every grant,
          which is the "something just landed" signal the token exists to give.
          `TenantAuditPanel`'s header has the full argument.
        */}
        <TenantCreditsPanel tenant={tenant} />

        {/* Slice 71: flows by status and rule segments — counts only. */}
        <TenantUsagePanel tenant={tenant} />

        <TenantBoundaryPanel tenant={tenant} />

        <TenantAuditPanel tenantId={tenant.id} reloadToken={historyToken} />
      </div>
    </AdminShell>
  );
}

/* -------------------------------------------------------------------------- */
/* State, at the top                                                           */
/* -------------------------------------------------------------------------- */

function TenantHeader({ tenant, refreshing }: { tenant: Tenant; refreshing: boolean }) {
  const state = subscriptionState(tenant);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={state.tone}>{state.label}</Badge>
          {refreshing && <Spinner size="sm" label="Re-reading this workspace" />}
        </div>
        <CardTitle>{tenant.name}</CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">{state.detail}</p>

        <dl className="flex flex-wrap gap-x-10 gap-y-4">
          <Fact label="Contact">
            <span className="font-mono text-[0.82rem]">{tenant.email}</span>
            <span className="block text-[0.82rem] text-muted">{tenant.phone}</span>
          </Fact>

          <Fact label="Subscription expires">
            <span className="whitespace-nowrap">{formatDate(tenant.validTill)}</span>
            <span className="block text-[0.82rem] text-muted">
              {formatRelative(tenant.validTill)} · {formatDateTime(tenant.validTill)}
            </span>
          </Fact>

          <Fact label="Features">
            <FeatureTags features={tenant.features} className="pt-1" />
          </Fact>
        </dl>
      </CardContent>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-[10rem] flex-col gap-1.5">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[0.92rem] leading-6">{children}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* What the last save did                                                      */
/* -------------------------------------------------------------------------- */

function SaveOutcome({ result }: { result: UpdateTenantResponse }) {
  if (result.changes.length === 0) {
    return (
      // `assertive`, unlike the success below: this is the outcome an operator
      // must not walk away from believing their correction landed.
      <Callout tone="info" live="assertive" title="Nothing was saved">
        <p>
          Every field submitted already held the value it was given, so no column moved and no audit
          row was written. That usually means somebody else made the same correction first, or the
          edit came out identical once the value was normalised — spaces trimmed from the ends, a
          country code upper-cased. The workspace above has been re-read from the API and is what is
          stored right now.
        </p>
      </Callout>
    );
  }

  return (
    <Callout
      tone="good"
      live="polite"
      title={`${String(result.changes.length)} ${
        result.changes.length === 1 ? 'field updated' : 'fields updated'
      }`}
    >
      <p>
        Recorded against your staff account with the reason you gave. It is in this
        workspace&rsquo;s change history at the bottom of this screen, permanently.
      </p>

      <dl className="mt-3 flex flex-col gap-1.5">
        {result.changes.map((change) => (
          <div key={change.field} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
            <dt className="text-[0.82rem] leading-6 text-muted sm:w-36 sm:shrink-0">
              {TENANT_FIELD_LABEL[change.field]}
            </dt>
            <dd className="min-w-0 text-[0.88rem] leading-6">
              <span className="text-muted line-through">
                {formatAuditValue(change.field, change.before, 'before')}
              </span>{' '}
              <span aria-hidden="true" className="text-muted">
                →
              </span>{' '}
              <span className="break-words text-text">
                {formatAuditValue(change.field, change.after, 'after')}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </Callout>
  );
}
